import { narrators, world, worldConfig } from '../../../game/demo';
import { getPOIFrom } from '../../utils';
import eventBus from '../../utils/emitter-event-bus';
import { MapConfig, MapID, NavigatorEventBus, POI, POI_ID } from '../navigator/navigator.model';
import { WorldEventBus } from '../world/world.model';
import { BUILDER_STORAGE_KEY, LONG_PRESS_THRESHOLD } from './builder.model';

export class Builder {
  private active = false;
  private worldPOIs = world;
  private currentMapConfig!: MapConfig;

  private navigatorElement = document.querySelector('.js-navigator') as HTMLElement;
  private worldElement!: HTMLElement;

  private toggleButton!: HTMLButtonElement;
  private exportButton!: HTMLButtonElement;
  private modal!: HTMLElement;

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private isPanning = false;
  private panStartX = 0;
  private panStartY = 0;
  private panStartTranslateX = 0;
  private panStartTranslateY = 0;

  private boundMouseDown: (e: MouseEvent | TouchEvent) => void;
  private boundMouseUp: (e: MouseEvent | TouchEvent) => void;
  private boundMouseMove: (e: MouseEvent | TouchEvent) => void;

  constructor() {
    this.boundMouseDown = this.onMouseDown.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);

    this.createModal();
    this.setupControls();
    this.loadFromStorage();

    eventBus.on(WorldEventBus.initialise, () => {
      this.worldElement = document.querySelector('.map-world') as HTMLElement;
    });

    eventBus.on(NavigatorEventBus.mapChanged, (mapId: MapID) => {
      this.currentMapConfig = worldConfig.maps.find((m) => m.id === mapId) ?? worldConfig.maps[0];
      this.worldElement = document.querySelector('.map-world') as HTMLElement;
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'b' || e.key === 'B') {
        if (this.modal.style.display !== 'flex') this.toggle();
      }
      if (e.key === 'Escape') this.closeModal();
    });
  }

  // ── Modal ──

  private createModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = 'builder-modal';
    this.modal.style.display = 'none';
    this.modal.innerHTML = `
      <div class="builder-modal__content">
        <h3 class="builder-modal__title">New POI</h3>
        <label>ID<input type="text" name="id" /></label>
        <label>Spotlight title<input type="text" name="spotlightTitle" /></label>
        <label>Spotlight description<input type="text" name="spotlightDesc" /></label>
        <label>Destinations <span style="font-size:10px;color:#666">(comma-separated IDs)</span><input type="text" name="destinations" /></label>
        <label>Narrator<select name="narratorId"><option value="">None</option></select></label>
        <div class="builder-modal__portal">
          <span style="font-size:12px;color:#aaa;">Portal</span>
          <div style="display:flex;gap:8px;">
            <label style="flex:1">Map<select name="portalMap"><option value="">None</option></select></label>
            <label style="flex:1">Target POI<input type="text" name="portalPOI" /></label>
          </div>
        </div>
        <div class="builder-modal__actions">
          <button class="builder-modal__delete" style="display:none">Delete</button>
          <span style="flex:1"></span>
          <button class="builder-modal__cancel">Cancel</button>
          <button class="builder-modal__save">Save</button>
        </div>
      </div>
    `;
    document.body.appendChild(this.modal);

    // Populate portal map dropdown
    const portalSelect = this.modal.querySelector('select[name="portalMap"]') as HTMLSelectElement;
    worldConfig.maps.forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m.id;
      opt.textContent = m.id;
      portalSelect.appendChild(opt);
    });

    // Populate narrator dropdown
    const narratorSelect = this.modal.querySelector('select[name="narratorId"]') as HTMLSelectElement;
    narrators.forEach((n) => {
      const opt = document.createElement('option');
      opt.value = n.id;
      opt.textContent = n.name;
      narratorSelect.appendChild(opt);
    });

    this.modal.querySelector('.builder-modal__cancel')!.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.builder-modal__save')!.addEventListener('click', () => this.saveModal());
    this.modal.querySelector('.builder-modal__delete')!.addEventListener('click', () => this.deleteModal());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.closeModal();
    });
  }

  private pendingPOIPos: [number, number] | null = null;
  private editingPOIId: POI_ID | null = null;
  private lastEditedPOIId: POI_ID | null = null;

  private openModalForNew(x: number, y: number): void {
    this.pendingPOIPos = [x, y];
    this.editingPOIId = null;

    const title = this.modal.querySelector('.builder-modal__title') as HTMLElement;
    title.textContent = 'New POI';

    const deleteBtn = this.modal.querySelector('.builder-modal__delete') as HTMLElement;
    deleteBtn.style.display = 'none';

    const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);
    const lastPOI = currentMapPOIs[currentMapPOIs.length - 1];
    const nearest = this.findNearestPOI(x, y, Infinity);

    const suggestions = new Set<string>();
    if (nearest) suggestions.add(nearest.id);
    if (lastPOI && lastPOI !== nearest) suggestions.add(lastPOI.id);

    this.getInput('id').value = this.getNextId();
    this.getInput('spotlightTitle').value = '';
    this.getInput('spotlightDesc').value = '';
    this.getInput('destinations').value = Array.from(suggestions).join(', ');
    this.getSelect('narratorId').value = '';
    this.getSelect('portalMap').value = '';
    this.getInput('portalPOI').value = '';

    this.modal.style.display = 'flex';
    this.getInput('id').focus();
    this.getInput('id').select();
  }

  private openModalForEdit(id: POI_ID): void {
    const poi = getPOIFrom(id);
    if (!poi) return;

    this.pendingPOIPos = null;
    this.editingPOIId = id;

    const title = this.modal.querySelector('.builder-modal__title') as HTMLElement;
    title.textContent = `Edit: ${id}`;

    const deleteBtn = this.modal.querySelector('.builder-modal__delete') as HTMLElement;
    deleteBtn.style.display = 'block';

    this.getInput('id').value = poi.id;
    this.getInput('spotlightTitle').value = poi.spotlight?.title || '';
    this.getInput('spotlightDesc').value = poi.spotlight?.description || '';
    this.getInput('destinations').value = poi.destination.join(', ');
    this.getSelect('narratorId').value = poi.narratorId || '';
    this.getSelect('portalMap').value = poi.portal?.targetMap || '';
    this.getInput('portalPOI').value = poi.portal?.targetPOI || '';

    this.modal.style.display = 'flex';
    this.getInput('spotlightTitle').focus();
  }

  private saveModal(): void {
    const id = this.getInput('id').value.trim();
    if (!id) return;

    const spotlightTitle = this.getInput('spotlightTitle').value.trim();
    const spotlightDesc = this.getInput('spotlightDesc').value.trim();
    const spotlight = spotlightTitle ? { title: spotlightTitle, description: spotlightDesc } : undefined;
    const destinations = this.getInput('destinations').value
      .split(',')
      .map((d) => d.trim())
      .filter(Boolean);

    const portalMap = this.getSelect('portalMap').value;
    const portalPOI = this.getInput('portalPOI').value.trim();
    const portal = portalMap && portalPOI ? { targetMap: portalMap, targetPOI: portalPOI } : undefined;

    const narratorId = this.getSelect('narratorId').value || undefined;

    if (this.editingPOIId) {
      const poi = this.worldPOIs.find((p) => p.id === this.editingPOIId);
      if (poi) {
        const oldId = poi.id;
        poi.id = id;
        poi.spotlight = spotlight;
        poi.destination = destinations;
        poi.portal = portal;
        poi.narratorId = narratorId;

        if (oldId !== id) {
          this.worldPOIs.forEach((p) => {
            p.destination = p.destination.map((d) => (d === oldId ? id : d));
          });
        }
        console.log('Updated POI:', JSON.parse(JSON.stringify(poi)));
        this.lastEditedPOIId = id;
      }
    } else if (this.pendingPOIPos) {
      const newPOI: POI = {
        id,
        mapId: this.currentMapConfig.id,
        pos: this.pendingPOIPos,
        destination: destinations,
        ...(spotlight && { spotlight }),
        ...(portal && { portal }),
        ...(narratorId && { narratorId }),
      };

      // Auto-link: add this POI to the destination list of linked POIs
      destinations.forEach((destId) => {
        const destPOI = this.worldPOIs.find((p) => p.id === destId);
        if (destPOI && !destPOI.destination.includes(id)) {
          destPOI.destination.push(id);
        }
      });

      this.worldPOIs.push(newPOI);
      console.log('Added POI:', JSON.parse(JSON.stringify(newPOI)));
      this.centerOnPOI(newPOI);
      this.lastEditedPOIId = newPOI.id;
    }

    this.saveToStorage();
    this.rebuildMarkers();
    this.closeModal();
  }

  private deleteModal(): void {
    if (!this.editingPOIId) return;

    const id = this.editingPOIId;

    // Remove destination references to this POI from other POIs
    this.worldPOIs.forEach((p) => {
      p.destination = p.destination.filter((d) => d !== id);
    });

    // Remove the POI itself
    const idx = this.worldPOIs.findIndex((p) => p.id === id);
    if (idx !== -1) {
      this.worldPOIs.splice(idx, 1);
      console.log('Deleted POI:', id);
    }

    if (this.lastEditedPOIId === id) this.lastEditedPOIId = null;

    this.saveToStorage();
    this.rebuildMarkers();
    this.closeModal();
  }

  private closeModal(): void {
    this.modal.style.display = 'none';
    this.pendingPOIPos = null;
    this.editingPOIId = null;
  }

  private getInput(name: string): HTMLInputElement {
    return this.modal.querySelector(`input[name="${name}"]`) as HTMLInputElement;
  }

  private getSelect(name: string): HTMLSelectElement {
    return this.modal.querySelector(`select[name="${name}"]`) as HTMLSelectElement;
  }

  // ── Controls ──

  private setupControls(): void {
    const controls = document.querySelector('.builder-controls') as HTMLElement;
    if (!controls) return;

    controls.style.display = 'flex';

    this.toggleButton = controls.querySelector('.builder-toggle') as HTMLButtonElement;
    this.exportButton = controls.querySelector('.builder-export') as HTMLButtonElement;

    this.toggleButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });
    this.exportButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.exportToClipboard();
    });
  }

  private toggle(): void {
    this.active = !this.active;
    this.toggleButton.classList.toggle('is-active', this.active);
    document.body.classList.toggle('builder-active', this.active);

    if (this.active) {
      this.navigatorElement.addEventListener('mousedown', this.boundMouseDown);
      this.navigatorElement.addEventListener('touchstart', this.boundMouseDown);
    } else {
      this.navigatorElement.removeEventListener('mousedown', this.boundMouseDown);
      this.navigatorElement.removeEventListener('touchstart', this.boundMouseDown);
      this.cleanupPan();
      this.startOnLastPOI();
    }
  }

  // ── Event handling ──

  private onMouseDown(event: MouseEvent | TouchEvent): void {
    if ((event.target as HTMLElement).closest('.builder-controls')) return;
    if (this.modal.style.display === 'flex') return;

    event.preventDefault();
    event.stopPropagation();

    const { clientX, clientY } = this.getClientXY(event);

    this.longPressTimer = setTimeout(() => {
      this.startPan(clientX, clientY);
    }, LONG_PRESS_THRESHOLD);

    document.addEventListener('mouseup', this.boundMouseUp);
    document.addEventListener('touchend', this.boundMouseUp);
    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('touchmove', this.boundMouseMove);
  }

  private onMouseUp(event: MouseEvent | TouchEvent): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;

      const { clientX, clientY } = this.getClientXY(event);
      this.handleClick(clientX, clientY);
    }

    this.cleanupPan();
    document.removeEventListener('mouseup', this.boundMouseUp);
    document.removeEventListener('touchend', this.boundMouseUp);
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('touchmove', this.boundMouseMove);
  }

  private onMouseMove(event: MouseEvent | TouchEvent): void {
    if (this.isPanning) {
      const { clientX, clientY } = this.getClientXY(event);
      const dx = clientX - this.panStartX;
      const dy = clientY - this.panStartY;
      this.worldElement.style.transition = 'none';
      this.worldElement.style.transform = `translate(${this.panStartTranslateX + dx}px, ${this.panStartTranslateY + dy}px)`;
    }
  }

  // ── Pan ──

  private startPan(clientX: number, clientY: number): void {
    this.longPressTimer = null;
    this.isPanning = true;
    this.panStartX = clientX;
    this.panStartY = clientY;

    const transform = this.worldElement.style.transform;
    const match = transform.match(/translate\((-?[\d.]+)px,\s*(-?[\d.]+)px\)/);
    this.panStartTranslateX = match ? parseFloat(match[1]) : 0;
    this.panStartTranslateY = match ? parseFloat(match[2]) : 0;

    this.worldElement.style.cursor = 'grabbing';
  }

  private cleanupPan(): void {
    this.isPanning = false;
    if (this.worldElement) {
      this.worldElement.style.cursor = '';
      this.worldElement.style.transition = '';
    }
  }

  // ── Click handling ──

  private clientToMapCoords(clientX: number, clientY: number): [number, number] {
    const rect = this.worldElement.getBoundingClientRect();
    const scaleX = this.currentMapConfig.size[0] / rect.width;
    const scaleY = this.currentMapConfig.size[1] / rect.height;
    return [
      Math.floor((clientX - rect.left) * scaleX),
      Math.floor((clientY - rect.top) * scaleY),
    ];
  }

  private findNearestPOI(mapX: number, mapY: number, maxRadius = 30): POI | null {
    const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);
    let nearest: POI | null = null;
    let nearestDist = Infinity;

    for (const poi of currentMapPOIs) {
      const dx = poi.pos[0] - mapX;
      const dy = poi.pos[1] - mapY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < maxRadius && dist < nearestDist) {
        nearest = poi;
        nearestDist = dist;
      }
    }
    return nearest;
  }

  private handleClick(clientX: number, clientY: number): void {
    const [mapX, mapY] = this.clientToMapCoords(clientX, clientY);
    const hitPOI = this.findNearestPOI(mapX, mapY);

    if (hitPOI) {
      console.log('POI:', JSON.parse(JSON.stringify(hitPOI)));
      this.openModalForEdit(hitPOI.id);
    } else {
      this.openModalForNew(mapX, mapY);
    }
  }

  // ── Helpers ──

  private centerOnPOI(poi: POI): void {
    const viewport = this.navigatorElement.getBoundingClientRect();
    const translateX = -(poi.pos[0] - viewport.width / 2);
    const translateY = -(poi.pos[1] - viewport.height * 0.72);
    this.worldElement.style.transition = 'transform 500ms ease-in-out';
    this.worldElement.style.transform = `translate(${translateX}px, ${translateY}px)`;
  }

  private getNextId(): string {
    const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);
    if (currentMapPOIs.length === 0) return 'poi-1';

    const lastId = currentMapPOIs[currentMapPOIs.length - 1].id;
    const match = lastId.match(/^(.*-)(\d+)$/);
    if (match) {
      return `${match[1]}${parseInt(match[2]) + 1}`;
    }
    return `${lastId}-1`;
  }

  private rebuildMarkers(): void {
    this.worldElement.querySelectorAll('.map-world__marker').forEach((el) => el.remove());

    const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);
    currentMapPOIs.forEach(({ id, pos }) => {
      const marker = document.createElement('div');
      marker.className = 'map-world__marker';
      marker.style.left = `${pos[0]}px`;
      marker.style.top = `${pos[1]}px`;
      marker.innerHTML = `<div id="${id}" class="map-world__marker-dot"></div>`;
      this.worldElement.appendChild(marker);
    });
  }

  private saveToStorage(): void {
    const data = this.worldPOIs.map(({ id, mapId, pos, destination, spotlight, narratorId, portal }) => ({
      id,
      mapId,
      pos,
      destination,
      ...(spotlight && { spotlight }),
      ...(narratorId && { narratorId }),
      ...(portal && { portal }),
    }));
    localStorage.setItem(BUILDER_STORAGE_KEY, JSON.stringify(data));
  }

  private loadFromStorage(): void {
    const stored = localStorage.getItem(BUILDER_STORAGE_KEY);
    if (!stored) return;

    const pois: POI[] = JSON.parse(stored);
    if (Array.isArray(pois) && pois.length > 0) {
      this.worldPOIs.length = 0;
      pois.forEach((p) => this.worldPOIs.push(p));
    }
  }

  private exportToClipboard(): void {
    const esc = (s: string): string => s.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
    const lines = this.worldPOIs.map((p) => {
      const parts = [
        `    id: '${esc(p.id)}'`,
        `    mapId: '${esc(p.mapId)}'`,
        `    pos: [${p.pos[0]}, ${p.pos[1]}]`,
        `    destination: [${p.destination.map((d) => `'${esc(d)}'`).join(', ')}]`,
      ];
      if (p.spotlight) {
        parts.push(`    spotlight: { title: '${esc(p.spotlight.title)}', description: '${esc(p.spotlight.description)}' }`);
      }
      if (p.narratorId) {
        parts.push(`    narratorId: '${esc(p.narratorId)}'`);
      }
      if (p.portal) {
        parts.push(`    portal: { targetMap: '${esc(p.portal.targetMap)}', targetPOI: '${esc(p.portal.targetPOI)}' }`);
      }
      return `  {\n${parts.join(',\n')},\n  }`;
    });

    const ts = `export const world: POI[] = [\n${lines.join(',\n')},\n];\n`;

    navigator.clipboard.writeText(ts).then(
      () => console.log('Exported to clipboard!'),
      (err) => console.error('Clipboard write failed:', err),
    );
  }

  private startOnLastPOI(): void {
    // Prefer the most recently edited POI; fall back to the last POI on the current map.
    let targetPOI: POI_ID | undefined;
    if (this.lastEditedPOIId && this.worldPOIs.some((p) => p.id === this.lastEditedPOIId)) {
      targetPOI = this.lastEditedPOIId;
    } else {
      const currentMapPOIs = this.worldPOIs.filter((p) => p.mapId === this.currentMapConfig.id);
      targetPOI = currentMapPOIs[currentMapPOIs.length - 1]?.id;
    }
    if (!targetPOI) return;

    this.worldElement?.remove();
    eventBus.emit(WorldEventBus.initialise, targetPOI);
  }

  private getClientXY(event: MouseEvent | TouchEvent): { clientX: number; clientY: number } {
    if (event instanceof MouseEvent) {
      return { clientX: event.clientX, clientY: event.clientY };
    }
    const touch = event.touches[0] || event.changedTouches[0];
    return { clientX: touch.clientX, clientY: touch.clientY };
  }
}
